import { countrySlugMap, countrySlugReverseMap } from "./localizedCountries";
import {
  localizedRoutes,
  newListingSubRoutes,
  myListingsSubRoutes,
  dynamicSlugTranslations,
  agencySubRoutes,
} from "./localizedRoutes";
import { NotificationRoute } from "@/types/types";
import {
  resolveDomainPathLocale,
  withVisibleLocalePrefix,
} from "@/lib/domainConfig";

const subRouteGroups: Record<string, Record<string, Record<string, string>>> = {
  "new-listing": newListingSubRoutes,
  "my-listings": myListingsSubRoutes,
  agency: agencySubRoutes,
};

/**
 * Get canonical route key from a translated slug array.
 * Example: "ro/anunt-nou/publica/123" -> "new-listing"
 */
export function getRouteKeyFromSlug(
  locale: string,
  slug: string | string[],
): string | null {
  // if (!slug || (Array.isArray(slug) && slug.length === 0)) return "home";
  if (!slug || (Array.isArray(slug) && slug.length === 0)) {
    return "home";
  }

  const first = norm(Array.isArray(slug) ? slug[0] : slug);

  // Case 2: country slug → localized home (/fr/roumanie, /en/romania)
  if (countrySlugReverseMap[locale]?.[first]) {
    return "country";
  }

  // const first = norm(Array.isArray(slug) ? slug[0] : slug);

  for (const [key, locales] of Object.entries(localizedRoutes)) {
    const translated = norm(locales[locale]);
    if (translated === first) return key;
  }

  return null;
}

/**
 * Get canonical subroute key if any.
 * Example: "ro/anunt-nou/publica/123" -> "publish"
 */
export function getSubRouteKey(
  locale: string,
  parentRoute: string,
  slug: string[],
): string | null {
  if (!slug || slug.length < 2) return null;
  const subRoutes = subRouteGroups[parentRoute];
  if (!subRoutes) return null;

  const second = norm(slug[1]);
  for (const [key, locales] of Object.entries(subRoutes)) {
    const candidate = norm(locales[locale]);
    if (candidate === second) return key;
  }

  return null;
}

/**
 * Build a localized path.
 * getLocalizedPath("ro", "my-account") → /ro/contul-meu
 * getLocalizedPath("ro", "listing", ["123"]) → /ro/anunt/123
 */
export function getLocalizedPath(
  locale: string,
  routeKey: string,
  extras?: string | string[],
) {
  const translated = localizedRoutes[routeKey]?.[locale];
  if (!translated) return `/`;
  const base = translated;
  let extraPath = "";
  if (Array.isArray(extras)) {
    const subRoutesForKey = subRouteGroups[routeKey];
    const updatedExtras = extras.map((extra, index) => {
      if (routeKey === "search" && index === 0) {
        return extra;
      }

      // 1️⃣ First check subroutes
      if (index === 0 && subRoutesForKey) {
        let matchedKey: string | undefined;
        for (const [key, locales] of Object.entries(subRoutesForKey)) {
          if (Object.values(locales).includes(extra)) {
            matchedKey = key;
            break;
          }
        }
        if (matchedKey) return subRoutesForKey[matchedKey][locale] ?? extra;
      }

      // 2️⃣ Then check dynamic slugs (like purpose, type, etc.)
      for (const [category, translations] of Object.entries(
        dynamicSlugTranslations,
      )) {
        let matchedKey: string | undefined;
        for (const [key, locales] of Object.entries(translations)) {
          if (Object.values(locales).includes(extra)) {
            matchedKey = key;
            break;
          }
        }
        if (matchedKey) {
          return translations[matchedKey][locale] ?? extra;
        }
      }

      // fallback
      return extra;
    });

    extraPath = updatedExtras.join("/");
  } else {
    extraPath = extras ?? "";
  }
  return `/${base}${extraPath ? `/${extraPath}` : ""}`;
}

/**
 * Build a localized subroute path.
 * Example:
 * getLocalizedSubPath("ro", "new-listing", "publish", "123")
 * → /ro/anunt-nou/publica/123
 */
export function getLocalizedSubPath(
  locale: string,
  parentRoute: string,
  subRouteKey: string,
  extras?: string | string[],
) {
  const base = localizedRoutes[parentRoute]?.[locale] ?? "";
  const sub =
    subRouteGroups[parentRoute]?.[subRouteKey]?.[locale] ?? subRouteKey;
  const extraPath = Array.isArray(extras) ? extras.join("/") : (extras ?? "");
  return `/${base}/${sub}${extraPath ? `/${extraPath}` : ""}`;
}

/**
 * Convenience wrapper for making hrefs in components.
 */
export function makeLocalizedHref(
  locale: string,
  routeKey: string,
  extras?: string | string[],
) {
  return getLocalizedPath(locale, routeKey, extras);
}

export function getLocalizedPathNoLocale(
  locale: string,
  routeKey: string,
  extras?: string | string[],
) {
  const translated = localizedRoutes[routeKey]?.[locale];
  if (!translated) return `/`;
  const base = translated;
  let extraPath = "";
  if (Array.isArray(extras)) {
    const subRoutesForKey = subRouteGroups[routeKey];
    const updatedExtras = extras.map((extra, index) => {
      if (routeKey === "search" && index === 0) {
        return extra;
      }

      // 1️⃣ First check subroutes
      if (subRoutesForKey) {
        let matchedKey: string | undefined;
        for (const [key, locales] of Object.entries(subRoutesForKey)) {
          if (Object.values(locales).includes(extra)) {
            matchedKey = key;
            break;
          }
        }
        if (matchedKey) return subRoutesForKey[matchedKey][locale] ?? extra;
      }

      // 2️⃣ Then check dynamic slugs (like purpose, type, etc.)
      for (const [category, translations] of Object.entries(
        dynamicSlugTranslations,
      )) {
        let matchedKey: string | undefined;
        for (const [key, locales] of Object.entries(translations)) {
          if (Object.values(locales).includes(extra)) {
            matchedKey = key;
            break;
          }
        }
        if (matchedKey) {
          return translations[matchedKey][locale] ?? extra;
        }
      }

      // fallback
      return extra;
    });

    extraPath = updatedExtras.join("/");
  } else {
    extraPath = extras ?? "";
  }
  return `/${base}${extraPath ? `/${extraPath}` : ""}`;
}

export function makeLocalizedHrefNoLocale(
  locale: string,
  routeKey: string,
  extras?: string | string[],
) {
  return getLocalizedPathNoLocale(locale, routeKey, extras);
}

export function buildNotificationHref(
  locale: string,
  route: NotificationRoute | undefined,
): string {
  if (!route) return "#";

  const { routeKey, subRouteKey, params } = route;

  let path: string;

  if (subRouteKey) {
    path = getLocalizedSubPath(locale, routeKey, subRouteKey);
  } else {
    path = makeLocalizedHref(locale, routeKey);
  }

  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams(params).toString();
    return `${path}?${search}`;
  }

  return path;
}

export function normalizeSlugToEnglish(slug: string): string {
  const s = norm(slug);
  for (const [, translations] of Object.entries(dynamicSlugTranslations)) {
    for (const [key, locales] of Object.entries(translations)) {
      // any locale value that equals s (after decode+normalize) wins
      if (Object.values(locales).some((val) => norm(val) === s)) {
        return key; // canonical English key
      }
    }
  }
  return slug; // If not found, return as is
}

export function translatePath(
  pathname: string,
  fromLocale: string,
  toLocale: string,
  searchParams?: string,
): string {
  const slugArray = pathname.split("/").filter(Boolean);

  // Case 1: global home (just "/")
  if (slugArray.length === 0) {
    return "/";
  }

  const currentRouteKey = getRouteKeyFromSlug(fromLocale, slugArray[0]);

  if (!currentRouteKey) {
    return "/";
  }

  let newHref: string;

  if (currentRouteKey === "country") {
    const currentSlug = norm(slugArray[0]);
    const countryCode = countrySlugReverseMap[fromLocale]?.[currentSlug];

    if (!countryCode) {
      newHref = "/";
    } else {
      const translatedSlug = countrySlugMap[countryCode][toLocale];
      newHref = `/${translatedSlug}`;
    }
  } else {
    const extras = slugArray.slice(1);
    newHref = makeLocalizedHrefNoLocale(toLocale, currentRouteKey, extras);
  }

  return searchParams ? `${newHref}?${searchParams}` : newHref;
}

const safeDecode = (v: string) => {
  try {
    // Only attempt to decode if it looks encoded
    return v && v.includes("%") ? decodeURIComponent(v) : v;
  } catch {
    return v; // if already decoded or malformed, keep as-is
  }
};

export const norm = (v?: string) =>
  v ? safeDecode(v).normalize("NFC") : (v ?? "");

export function makePublicHref({
  domain,
  locale,
  pathname,
}: {
  domain: string;
  locale: string;
  pathname: string;
}) {
  return withVisibleLocalePrefix({
    domain,
    locale,
    pathname,
  });
}

export function makeLocalizedPublicHref(
  domain: string,
  locale: string,
  routeKey: string,
  extras?: string | string[],
) {
  const pathname = makeLocalizedHrefNoLocale(locale, routeKey, extras);

  return makePublicHref({
    domain,
    locale,
    pathname,
  });
}

export function stripVisibleLocalePrefix({
  domain,
  pathname,
}: {
  domain: string;
  pathname: string;
}) {
  return resolveDomainPathLocale({
    domain,
    pathname,
  }).pathnameWithoutLocalePrefix;
}
